import classNames from 'classnames';
import type { Delta, Range } from 'quill/core';
import Quill from 'quill/core';
import type { ReactNode } from 'react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import DeltaPreview from '../../../components/DeltaPreview';
import Logger from '../../../utils/Logger';
import { containSelection } from '../../utils/UserSelectContainer';
import { findQuillByNode } from '../../utils/findByNode';
import { CELL_IDENTITY_ATTRIBUTE } from '../constants';
import type { CellBorders } from '../getCellBordersForSelection';
import type { TableDocumentCell } from '../types';
import { parseCellIdentity } from '../utils';
import ColumnResizeHandler from './ColumnResizeHandler';
import * as styles from './TableCell.module.scss';
import TableCellEditor from './TableCellEditor';

const NEWLINE_CHAR_LENGTH = 1;

const getRangeOffset = (
  range: ReturnType<Selection['getRangeAt']>,
  node: Node,
  offset: number,
) => {
  const element = node instanceof HTMLElement ? node : node.parentElement;
  const closestBlock = element?.closest('p, div');
  if (!closestBlock) return 0;

  const preOffsetRange = range.cloneRange();
  preOffsetRange.selectNodeContents(closestBlock);
  preOffsetRange.setEnd(node, offset);
  let rangeOffset = preOffsetRange.toString().length;
  let previousBlock = closestBlock?.previousElementSibling;
  while (previousBlock) {
    rangeOffset +=
      (previousBlock.textContent?.length ?? 0) + NEWLINE_CHAR_LENGTH;
    previousBlock = previousBlock.previousElementSibling;
  }
  return rangeOffset;
};

const nativeSelectionToRange = (
  native: ReturnType<Selection['getRangeAt']>,
) => {
  const startOffset = getRangeOffset(
    native,
    native.startContainer,
    native.startOffset,
  );
  const endOffset = native.collapsed
    ? startOffset
    : getRangeOffset(native, native.endContainer, native.endOffset);
  return { index: startOffset, length: endOffset - startOffset };
};

const attributesSupportedByPreview = new Set([
  'author',
  'background',
  'bold',
  'code',
  'color',
  'highlight',
  'italic',
  'link',
  'strike',
]);

const shouldAlwaysRenderedWithQuill = (content?: Delta['ops']) => {
  if (!content) return false;
  return content.some((op) => {
    // Always render embeds with editor to avoid layout shifts
    // when switching from preview to editor due to resources loading.
    if (typeof op.insert === 'object') return true;

    return op.attributes
      ? Object.keys(op.attributes).some(
          (attribute) => !attributesSupportedByPreview.has(attribute),
        )
      : false;
  });
};

export interface TableCellProps extends CellBorders {
  cell?: TableDocumentCell;
  focused: boolean;
  rowId: string;
  columnId: string;
  readOnly: boolean;
  identity: string;
  shouldRenderWithQuill: boolean;
  onContentChange: (
    { rowId, columnId }: { rowId: string; columnId: string },
    delta: Delta,
  ) => void;
  onSelectionChange: (value: {
    identity: string;
    range: Range | null;
    yChanged: boolean;
  }) => void;
  onColumnResizeStart: (
    columnId: string,
    initial: {
      width: number;
      offset: number;
    },
  ) => void;
  resizing: boolean;
  children: ReactNode;
  onLoadMoreRows?: () => void;
  isEmbedded: boolean;
}

// It's expensive to re-render cells when table component re-renders because
// a table normally has many cell components.
// The performance gain is significant when the table renders frequently, ex
// during column resizing.
const TableCell = memo(
  ({
    focused,
    cell,
    rowId,
    columnId,
    readOnly,
    identity,
    shouldRenderWithQuill,
    onContentChange,
    onSelectionChange,
    onColumnResizeStart,
    resizing,
    children,
    inRange,
    leftBorder,
    rightBorder,
    topBorder,
    bottomBorder,
    onLoadMoreRows,
    isEmbedded,
  }: TableCellProps) => {
    const [rowIndex, columnIndex] = parseCellIdentity(identity);

    const handleContentChange = useCallback(
      (delta: Delta) => {
        onContentChange({ rowId, columnId }, delta);
      },
      [rowId, columnId, onContentChange],
    );

    const handleSelectionChange = useCallback(
      (range: Range | null, yChanged: boolean) => {
        if (range) onSelectionChange({ identity, range, yChanged });
      },
      [identity, onSelectionChange],
    );

    const containerRef = useRef<HTMLTableCellElement | null>(null);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const [isConvertedToQuill, setIsConvertedToQuill] = useState(
      shouldRenderWithQuill,
    );

    const isRenderedWithQuill = useMemo(
      () =>
        isConvertedToQuill ||
        shouldRenderWithQuill ||
        shouldAlwaysRenderedWithQuill(cell?.content),
      [isConvertedToQuill, shouldRenderWithQuill, cell?.content],
    );

    useEffect(() => {
      if (isRenderedWithQuill) return undefined;

      const listener = () => {
        const selection = document.getSelection();
        if (!selection?.rangeCount) return;
        const native = selection.getRangeAt(0);
        const isSelectionInsideCellContent =
          previewContainerRef.current?.contains(native.commonAncestorContainer);
        if (!isSelectionInsideCellContent) return;

        const range = nativeSelectionToRange(native);
        flushSync(() => {
          setIsConvertedToQuill(true);
        });
        const container = containerRef.current?.querySelector('.ql-container');
        if (container) {
          const quill = findQuillByNode(container);
          quill?.setSelection(range, Quill.sources.USER);
        }
      };

      document.addEventListener('selectionchange', listener);
      return () => {
        document.removeEventListener('selectionchange', listener);
      };
    }, [isRenderedWithQuill]);

    useEffect(() => {
      const container = containerRef.current;
      if (isRenderedWithQuill || readOnly || !container) return undefined;
      const listener = () => setIsConvertedToQuill(true);
      container.addEventListener('dragover', listener);
      return () => {
        container.removeEventListener('dragover', listener);
      };
    }, [isRenderedWithQuill, readOnly]);

    const isInLastTruncatedRow = !!onLoadMoreRows;
    return (
      <td
        ref={containerRef}
        data-table-column={rowIndex === 0 ? columnIndex : undefined}
        {...{ [CELL_IDENTITY_ATTRIBUTE]: identity }}
        onClick={onLoadMoreRows}
        className={classNames(styles.container, {
          [styles.inLastTruncatedRow]: isInLastTruncatedRow,
          [styles.firstRow]: rowIndex === 0,
          [styles.firstColumn]: columnIndex === 0,
          [styles.leftBorder]: leftBorder,
          [styles.rightBorder]: rightBorder,
          [styles.topBorder]: topBorder,
          [styles.bottomBorder]: bottomBorder,
          [styles.inRange]: inRange,
        })}
      >
        {isRenderedWithQuill ? (
          <TableCellEditor
            focused={focused}
            readOnly={readOnly || isInLastTruncatedRow}
            containerClassName={styles.editorContainer}
            isEmbedded={isEmbedded}
            // @ts-expect-error
            initialContent={cell?.content}
            onContentChange={handleContentChange}
            // @ts-expect-error
            onSelectionChange={handleSelectionChange}
          />
        ) : (
          <div
            ref={previewContainerRef}
            contentEditable={readOnly ? 'false' : 'true'}
            suppressContentEditableWarning
            onBeforeInput={(e) => {
              e.preventDefault();
              Logger.warn('Trying to edit a static cell');
            }}
            onClick={() => {
              if (readOnly && previewContainerRef.current) {
                containSelection(previewContainerRef.current);
              }
            }}
            className={classNames(
              styles.deltaPreviewContainer,
              styles.editorContainer,
            )}
          >
            {cell?.content && (
              <DeltaPreview
                link
                content={cell.content}
                typography="postWithNoTitleLine"
              />
            )}
          </div>
        )}
        <div className={classNames({ [styles.hideControls]: readOnly })}>
          <ColumnResizeHandler
            firstRow={rowIndex === 0}
            resizing={resizing}
            onResizeStart={(initial) => onColumnResizeStart(columnId, initial)}
          />
        </div>
        {children}
      </td>
    );
  },
);

export default TableCell;
